import { useState, type ReactNode } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useLocation, useNavigate } from 'react-router-dom'
import { db } from '../lib/db'
import { isOwner, useApp } from '../lib/store'
import { useNow } from '../lib/hooks'
import { PAGE_HEAD_SLOT } from './PageHead'
import { ScooterGlyph } from './ScooterIcon'
import { SyncBadge } from './SyncBadge'

/**
 * Khung ngoài của bộ giao diện Modern: thanh trên, dải điều hướng bên trái, chỗ cho
 * màn hình đang mở.
 *
 * Dải bên trái <b>thu gọn sẵn</b> và chỉ rộng ra khi rê chuột vào. Máy tính tiền thì thứ
 * đáng chiếm chỗ là bàn và món, không phải một danh sách menu ngồi đó cả ngày. Lúc rộng
 * ra nó nổi đè lên nội dung chứ không đẩy, để bố cục bên phải không nhảy một cái mỗi lần
 * con chuột đi ngang.
 *
 * Màn hình hẹp thì giấu hẳn dải này. Điện thoại đã có nút lớn ở màn hình chính và nút
 * quay lại trên từng màn hình rồi — nhét thêm một cột nữa vào màn 5 inch là lấy mất chỗ
 * của chính thứ người ta cần bấm.
 */

interface NavItem {
  to: string
  label: string
  icon: ReactNode
  ownerOnly?: boolean
  /** Tô màu cho biểu tượng: ba lối vào bán hàng phải bắt mắt hơn phần còn lại. */
  tone?: 'brand' | 'amber' | 'clay'
  /** Mục này còn sáng khi đang ở các màn hình con của nó. */
  match?: (path: string, takeaway: boolean) => boolean
}

/*
 * Màn hình gọi món và màn hình thu tiền dùng chung một đường dẫn cho cả hai lối bán.
 *
 * Nên đường dẫn một mình không nói được mục nào đang sáng: /order/<mã> có thể là bàn số 3,
 * mà cũng có thể là một lượt khách đứng đợi mang về. Người bán bấm "Mang về" đi vào rồi
 * thấy "Tại bàn" sáng lên là mất dấu mình đang đứng ở đâu.
 *
 * Trả lời bằng chính cái đơn đang mở chứ không bằng một mẩu trạng thái truyền tay: mở lại
 * app hay dán thẳng đường dẫn vào trình duyệt thì vẫn ra đúng.
 */
const SHARED = (p: string) => p.startsWith('/order/') || p.startsWith('/pay/')

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

const Icon = ({ d }: { d: string }) => (
  <svg viewBox="0 0 24 24" className="rail-glyph" aria-hidden="true" focusable="false">
    <path d={d} {...stroke} />
  </svg>
)

/*
 * Ba nhóm, cách nhau một khoảng rõ ràng.
 *
 * Trước đây tám mục xếp đều nhau thành một cột, và mắt phải đọc chữ mới tìm ra chỗ cần
 * bấm. Giờ chia theo việc: trên cùng là đường về nhà, giữa là hai lối vào bán hàng —
 * thứ người bán bấm suốt mười hai tiếng, nên biểu tượng được tô màu — dưới là mấy màn
 * hình mở vài lần một ngày.
 *
 * Bán tại bàn màu thương hiệu, bán nhanh màu hổ phách, cùng tông với ô hình trên chính
 * hai màn hình ấy. Bấm quen rồi thì chỉ cần liếc thấy vệt màu, khỏi đọc chữ.
 */
const NAV_GROUPS: NavItem[][] = [
[
  {
    to: '/',
    label: 'Trang chủ',
    icon: <Icon d="M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1z" />,
  },
],
[
  {
    to: '/tables',
    label: 'Tại bàn',
    tone: 'brand',
    icon: <Icon d="M3 9h18M5 9V6.5A1.5 1.5 0 0 1 6.5 5h11A1.5 1.5 0 0 1 19 6.5V9M6 9v10M18 9v10M9.5 13h5" />,
    match: (p, takeaway) => p.startsWith('/tables') || (SHARED(p) && !takeaway),
  },
  {
    to: '/quick',
    label: 'Bán nhanh',
    tone: 'amber',
    icon: <Icon d="M13 3 5 13.5h5.5L11 21l8-10.5h-5.5z" />,
  },
  {
    to: '/takeaway',
    label: 'Mang về',
    tone: 'clay',
    match: (p, takeaway) => p.startsWith('/takeaway') || (SHARED(p) && takeaway),
    icon: <ScooterGlyph className="rail-glyph" />,
  },
],
[
  {
    to: '/history',
    label: 'Hoá đơn',
    icon: <Icon d="M6 3h12v18l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6" />,
  },
  {
    to: '/expense',
    label: 'Nhập hàng',
    icon: <Icon d="M4 7h16l-1.2 12.1a1 1 0 0 1-1 .9H6.2a1 1 0 0 1-1-.9zM9 7V5.5a3 3 0 0 1 6 0V7" />,
  },
  {
    to: '/settings/products',
    label: 'Sản phẩm',
    icon: <Icon d="M7 8h9a3 3 0 0 1 0 6h-1M7 8v9a3 3 0 0 0 3 3h3a3 3 0 0 0 3-3v-3M4 20h14" />,
    ownerOnly: true,
  },
  {
    to: '/report',
    label: 'Báo cáo',
    icon: <Icon d="M5 20V11M12 20V4M19 20v-6M3 20h18" />,
    ownerOnly: true,
  },
  {
    to: '/settings',
    label: 'Cài đặt',
    icon: (
      <svg viewBox="0 0 24 24" className="rail-glyph" aria-hidden="true" focusable="false">
        <circle cx="12" cy="12" r="3.2" {...stroke} />
        <path
          d="M12 3.5v2M12 18.5v2M20.5 12h-2M5.5 12h-2M18 6l-1.4 1.4M7.4 16.6 6 18M18 18l-1.4-1.4M7.4 7.4 6 6"
          {...stroke}
        />
      </svg>
    ),
  },
],
]

const DAYS = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7']
const two = (n: number) => String(n).padStart(2, '0')

export function AppShell({ children }: { children: ReactNode }) {
  const nav = useNavigate()
  const { pathname } = useLocation()
  const owner = isOwner(useApp((s) => s.session?.role))

  // Đồng hồ trên thanh chạy theo phút. Đúng giây thì đẹp nhưng vẽ lại 60 lần mỗi phút
  // trên một cái máy Android rẻ đang mở sẵn màn hình bán hàng là phí pin.
  const now = new Date(useNow(30_000))

  /*
   * Mở hay thu là do người dùng bấm, và nhớ lại cho lần sau.
   *
   * Trước đây dải tự bung ra khi rê chuột. Bỏ hẳn: con chuột đi ngang qua suốt cả ngày,
   * mỗi lần đi ngang là cả bảng bên phải giật một cái. Một cái nút bấm thì chỉ đổi khi
   * người ta muốn nó đổi.
   */
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem('sigopos:rail') === 'open'
    } catch {
      return false
    }
  })

  function toggleRail() {
    setOpen((v) => {
      try {
        localStorage.setItem('sigopos:rail', v ? 'closed' : 'open')
      } catch {
        /* máy chặn quyền lưu trữ thì thôi, phiên này vẫn đổi được */
      }
      return !v
    })
  }

  /** Đơn đang mở trên màn hình dùng chung có thuộc quầy mang về không. */
  const takeaway = useLiveQuery(
    async () => {
      if (!SHARED(pathname)) return false
      const order = await db.orders.get(pathname.split('/')[2] ?? '')
      if (!order?.tableId) return false
      return (await db.diningTables.get(order.tableId))?.isTakeaway ?? false
    },
    [pathname],
    false,
  )

  const groups = NAV_GROUPS.map((g) => g.filter((n) => !n.ownerOnly || owner)).filter(
    (g) => g.length > 0,
  )
  const active = (n: NavItem) =>
    n.match ? n.match(pathname, takeaway) : pathname === n.to

  return (
    <div className="shell">
      <header className="topbar">
        <button
          type="button"
          className="rail-switch"
          onClick={toggleRail}
          aria-expanded={open}
          aria-controls="rail"
          aria-label={open ? 'Thu gọn thanh điều hướng' : 'Mở rộng thanh điều hướng'}
          title={open ? 'Thu gọn menu' : 'Mở rộng menu'}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M4 7h16M4 12h16M4 17h16" {...stroke} />
          </svg>
        </button>

        {/*
          Chỗ của tiêu đề trang. Trống trên màn hình chính — ở đó dải chào đã nói đủ tên
          quán và hôm nay bán được bao nhiêu, thêm một dòng nữa chỉ là nhắc lại.
        */}
        <div id={PAGE_HEAD_SLOT} className="topbar-head" />

        {/*
          Logo đứng thay chỗ khi màn hình không có tiêu đề riêng — chỉ còn màn hình chính.
          Ở đó thanh trên vốn trống trơn từ nút ☰ tới tận đồng hồ, mà một dải trống dài
          nửa mét ngang đầu màn hình thì trông như app chưa tải xong.
        */}
        <button
          type="button"
          className="topbar-brand"
          onClick={() => nav('/')}
          aria-label="Về trang chủ"
        >
          <span className="brand-mark" aria-hidden="true">
            <svg viewBox="0 0 32 32" focusable="false">
              <rect width="32" height="32" rx="9" fill="var(--brand)" />
              <path
                d="M23 9c0 7-4.2 11-9.4 11H10c0-6.6 4.4-11 9.6-11z"
                fill="var(--on-brand)"
                opacity="0.92"
              />
              <path d="M9 24c1.6-5.2 4.6-8.6 8.6-10.6" stroke="var(--on-brand)" strokeWidth="1.8" fill="none" strokeLinecap="round" />
            </svg>
          </span>
          <span className="brand-name">
            Sigo<b>POS</b>
          </span>
        </button>

        <div className="topbar-right">
          <SyncBadge />
          <div className="topbar-clock">
            <b className="num">
              {two(now.getHours())}:{two(now.getMinutes())}
            </b>
            <small className="num">
              {DAYS[now.getDay()]}, {two(now.getDate())}/{two(now.getMonth() + 1)}/{now.getFullYear()}
            </small>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={() => nav('/settings')}
            aria-label="Cài đặt"
          >
            ⚙
          </button>
        </div>
      </header>

      <div className="shell-main">
        <nav id="rail" className={`rail${open ? ' is-open' : ''}`} aria-label="Điều hướng">
          {groups.map((group) => (
          <ul className="rail-list" key={group[0]!.to}>
            {group.map((n) => (
              <li key={n.to}>
                <button
                  type="button"
                  className={`rail-item${n.tone ? ` is-${n.tone}` : ''}${active(n) ? ' is-on' : ''}`}
                  onClick={() => nav(n.to)}
                  aria-current={active(n) ? 'page' : undefined}
                  title={n.label}
                >
                  {n.icon}
                  <span className="rail-label">{n.label}</span>
                </button>
              </li>
            ))}
          </ul>
          ))}

          <div className="rail-foot">
            {/* Chỉ hiện khi dải mở rộng. Một góc nhỏ có hồn ở chỗ trống, không chắn gì cả. */}
            <div className="rail-wish-art" aria-hidden="true">
              <svg viewBox="0 0 80 64" className="rail-plant" focusable="false">
                <path d="M40 62c0-16 6-27 18-33" stroke="var(--brand)" strokeWidth="2" fill="none" strokeLinecap="round" opacity=".5" />
                <path d="M58 29c-7 1-11 5-13 11 7 1 12-3 13-11z" fill="var(--brand)" opacity=".38" />
                <path d="M40 62c0-13-4-22-13-27" stroke="var(--brand)" strokeWidth="2" fill="none" strokeLinecap="round" opacity=".5" />
                <path d="M27 35c6 2 9 6 10 12-6 0-10-4-10-12z" fill="var(--brand)" opacity=".28" />
                <path d="M26 62h28l-3 2H29z" fill="var(--brand)" opacity=".3" />
              </svg>
              <p className="rail-wish">
                Chúc bạn
                <br />
                bán hàng tốt!
              </p>
            </div>

            {/*
              Logo ngồi ở chân dải, không ngồi trên thanh trên nữa.
              Nó là thứ nhận diện sản phẩm, không phải thứ người bán cần đọc khi đang gõ
              đơn — mà chỗ trên thanh trên thì đáng giá hơn nhiều: giờ nó mang tên màn
              hình đang mở và nút quay về.
            */}
            <button
              type="button"
              className="rail-brand"
              onClick={() => nav('/')}
              aria-label="Về trang chủ"
              title="Về trang chủ"
            >
              <span className="brand-mark" aria-hidden="true">
                <svg viewBox="0 0 32 32" focusable="false">
                  <rect width="32" height="32" rx="9" fill="var(--brand)" />
                  <path
                    d="M23 9c0 7-4.2 11-9.4 11H10c0-6.6 4.4-11 9.6-11z"
                    fill="var(--on-brand)"
                    opacity="0.92"
                  />
                  <path d="M9 24c1.6-5.2 4.6-8.6 8.6-10.6" stroke="var(--on-brand)" strokeWidth="1.8" fill="none" strokeLinecap="round" />
                </svg>
              </span>
              <span className="brand-name">
                Sigo<b>POS</b>
              </span>
            </button>
          </div>
        </nav>

        <div className="shell-content">{children}</div>
      </div>
    </div>
  )
}
